import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { Share2, Link2, Loader2 } from "lucide-react";

const APP_URL = "https://touslesdeux.com";

export function ShareScore({ score, label, moduleTitle, scoreColor = "#C86B5E" }) {
  const cardRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  const shareText = `${label} · ${moduleTitle} : ${score}/100 ✨ Préparez votre couple avec Classes Prénuptiales — ${APP_URL}`;
  const dash = (score / 100) * 502.4;

  const buildImage = async () => {
    const canvas = await html2canvas(cardRef.current, {
      backgroundColor: null,
      scale: 2,
      useCORS: true,
      logging: false,
    });
    return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const blob = await buildImage();
      const file = blob ? new File([blob], "score-alignement.png", { type: "image/png" }) : null;

      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Notre parcours à deux", text: shareText });
      } else if (navigator.share) {
        await navigator.share({ title: "Notre parcours à deux", text: shareText, url: APP_URL });
      } else if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "score-alignement.png";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Image enregistrée. Partagez-la où vous voulez !");
      } else {
        toast.error("Partage indisponible sur cet appareil.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        toast.error("Le partage a été interrompu.");
      }
    } finally {
      setSharing(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}`);
      toast.success("Texte copié dans le presse-papier.");
    } catch (e) {
      toast.error("Impossible de copier le lien.");
    }
  };

  return (
    <>
      <div className="flex items-center justify-center gap-3 flex-wrap" data-html2canvas-ignore>
        <button
          data-testid="share-score-button"
          onClick={handleShare}
          disabled={sharing}
          className="inline-flex items-center gap-2 text-sm text-white bg-[#C86B5E] hover:bg-[#B35A4E] rounded-full px-5 py-2.5 transition-colors disabled:opacity-50"
        >
          {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
          {sharing ? "Préparation…" : "Partager mon score"}
        </button>
        <button
          data-testid="copy-score-link-button"
          onClick={copyLink}
          className="inline-flex items-center gap-2 text-sm text-[#5C5754] hover:text-[#C86B5E] border border-[#E6E2DD] hover:border-[#C86B5E] rounded-full px-5 py-2.5 transition-colors"
        >
          <Link2 className="w-4 h-4" /> Copier le lien
        </button>
      </div>

      {/* Carte visuelle générée (hors écran, via portail hors du contentRef PDF) */}
      {createPortal(
        <div
          ref={cardRef}
          aria-hidden="true"
          style={{
          position: "fixed",
          left: "-99999px",
          top: 0,
          width: "600px",
          height: "600px",
          background: "linear-gradient(160deg, #FAF6F0 0%, #F3E7DE 100%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "56px 48px",
          boxSizing: "border-box",
          fontFamily: "'Outfit', sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "15px",
              letterSpacing: "5px",
              textTransform: "uppercase",
              color: "#C86B5E",
              fontWeight: 600,
            }}
          >
            Classes Prénuptiales
          </div>
          <div style={{ fontSize: "13px", letterSpacing: "3px", color: "#8A8480", marginTop: "6px" }}>
            LE PARCOURS D'AVANT-MARIAGE
          </div>
        </div>

        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              letterSpacing: "4px",
              textTransform: "uppercase",
              color: "#8A8480",
              fontWeight: 600,
              marginBottom: "18px",
            }}
          >
            {label}
          </div>
          <div style={{ position: "relative", width: "230px", height: "230px", margin: "0 auto" }}>
            <svg width="230" height="230" style={{ transform: "rotate(-90deg)" }}>
              <circle cx="115" cy="115" r="100" stroke="#EADFD5" strokeWidth="14" fill="none" />
              <circle
                cx="115"
                cy="115"
                r="100"
                stroke={scoreColor}
                strokeWidth="14"
                fill="none"
                strokeDasharray={`${(score / 100) * 628.3} 628.3`}
                strokeLinecap="round"
              />
            </svg>
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "84px",
                  lineHeight: 1,
                  color: scoreColor,
                  fontWeight: 600,
                }}
              >
                {score}
              </span>
              <span style={{ fontSize: "13px", letterSpacing: "3px", textTransform: "uppercase", color: "#8A8480", marginTop: "4px" }}>
                sur 100
              </span>
            </div>
          </div>
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "30px",
              color: "#2C2A29",
              marginTop: "22px",
              fontStyle: "italic",
            }}
          >
            {moduleTitle}
          </div>
        </div>

        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "18px", color: "#5C5754" }}>Préparez votre oui, à deux.</div>
          <div style={{ fontSize: "15px", letterSpacing: "2px", color: "#C86B5E", marginTop: "6px", fontWeight: 600 }}>
            touslesdeux.com
          </div>
        </div>
        </div>,
        document.body
      )}
    </>
  );
}
